import React, { useEffect, useMemo, useRef, useState } from "react";
import { authFetch } from "../../auth";

const MAX_TRACK_POINTS = 1500;
const INTERPOLATE_STEPS = 4;

function toLocalDateTimeText(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("zh-CN", { hour12: false });
}

function toDateTimeLocalInputValue(value) {
  const dt = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return [
    dt.getFullYear(),
    pad(dt.getMonth() + 1),
    pad(dt.getDate())
  ].join("-") + `T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

function getDefaultDateTimeRange() {
  const end = new Date();
  const start = new Date(end);
  start.setMonth(start.getMonth() - 1);
  return {
    start: toDateTimeLocalInputValue(start),
    end: toDateTimeLocalInputValue(end)
  };
}

function downsamplePoints(rows, maxPoints) {
  if (!rows || rows.length <= maxPoints) return rows || [];
  const stride = Math.ceil(rows.length / maxPoints);
  const sampled = rows.filter((_, idx) => idx % stride === 0);
  const last = rows[rows.length - 1];
  if (sampled[sampled.length - 1] !== last) sampled.push(last);
  return sampled;
}

function interpolatePoints(rows, steps) {
  if (!rows || rows.length <= 1 || steps <= 1) return rows || [];
  const result = [];
  for (let i = 0; i < rows.length - 1; i += 1) {
    const a = rows[i];
    const b = rows[i + 1];
    const aLng = Number(a.longitude);
    const aLat = Number(a.latitude);
    const bLng = Number(b.longitude);
    const bLat = Number(b.latitude);
    result.push(a);
    for (let s = 1; s < steps; s += 1) {
      const t = s / steps;
      result.push({
        ...a,
        longitude: aLng + (bLng - aLng) * t,
        latitude: aLat + (bLat - aLat) * t
      });
    }
  }
  result.push(rows[rows.length - 1]);
  return result;
}

export function TrackPlayback() {
  const defaultTimeRange = useMemo(() => getDefaultDateTimeRange(), []);
  const [deviceId, setDeviceId] = useState("");
  const [deviceNo, setDeviceNo] = useState("");
  const [start, setStart] = useState(defaultTimeRange.start);
  const [end, setEnd] = useState(defaultTimeRange.end);
  const [points, setPoints] = useState([]);
  const [rawPointCount, setRawPointCount] = useState(0);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [index, setIndex] = useState(0);
  const [mapError, setMapError] = useState("");

  const mapRef = useRef(null);
  const polylineRef = useRef(null);
  const markerRef = useRef(null);
  const timerRef = useRef(null);

  const currentPoint = useMemo(() => {
    if (!points.length) return null;
    return points[Math.min(index, points.length - 1)];
  }, [points, index]);

  useEffect(() => {
    loadGoogleMapScript()
      .then(() => initMap())
      .catch((e) => setMapError(e?.message || "地图加载失败"));
    return () => {
      stopTimer();
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps) return;
    drawTrack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [points]);

  useEffect(() => {
    if (!mapRef.current || !window.google?.maps || !currentPoint) return;
    updatePlaybackMarker(currentPoint);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPoint]);

  async function queryTrack(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    setPlaying(false);
    stopTimer();
    try {
      const startUtc = new Date(start).toISOString();
      const endUtc = new Date(end).toISOString();
      const qs = new URLSearchParams({ start: startUtc, end: endUtc, page: "1", pageSize: "5000" });
      if (deviceId.trim()) qs.set("deviceId", deviceId.trim());
      if (deviceNo.trim()) qs.set("deviceNo", deviceNo.trim());
      if (!qs.has("deviceId") && !qs.has("deviceNo")) {
        throw new Error("设备ID和设备号至少填写一个。");
      }

      const res = await authFetch(`/api/tracks?${qs.toString()}`);
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "查询失败");
      }
      const data = await res.json();
      const rows = (data.items || []).filter((x) => x.longitude != null && x.latitude != null);
      setRawPointCount(rows.length);
      const sampled = downsamplePoints(rows, MAX_TRACK_POINTS);
      const smooth = interpolatePoints(sampled, INTERPOLATE_STEPS);
      setPoints(smooth);
      setIndex(0);
    } catch (err) {
      setError(err.message || "查询失败");
    } finally {
      setLoading(false);
    }
  }

  function loadGoogleMapScript() {
    return new Promise((resolve, reject) => {
      if (window.google?.maps) {
        resolve();
        return;
      }
      const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
      if (!apiKey) {
        reject(new Error("未配置 REACT_APP_GOOGLE_MAPS_API_KEY"));
        return;
      }
      const cbName = "__trackGoogleMapReady__";
      const script = document.createElement("script");
      script.src = `https://www.google.com.hk/maps/api/js?language=zh-CN&key=${encodeURIComponent(apiKey)}&callback=${cbName}&v=3.exp&sensor=false`;
      script.async = true;
      script.onerror = () => reject(new Error("Google 地图脚本加载失败"));
      window[cbName] = () => {
        delete window[cbName];
        resolve();
      };
      document.head.appendChild(script);
    });
  }

  function initMap() {
    if (!window.google?.maps) return;
    const map = new window.google.maps.Map(document.getElementById("trackMapContainer"), {
      center: { lng: 114.173355, lat: 22.335468 },
      zoom: 12,
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true
    });
    mapRef.current = map;
  }

  function drawTrack() {
    const map = mapRef.current;
    if (!map) return;

    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }
    if (markerRef.current && !points.length) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (!points.length) return;

    const path = points.map((p) => ({ lng: Number(p.longitude), lat: Number(p.latitude) }));
    polylineRef.current = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: "#1f78ff",
      strokeOpacity: 0.9,
      strokeWeight: 4,
      map
    });

    const bounds = new window.google.maps.LatLngBounds();
    path.forEach((pt) => bounds.extend(pt));
    map.fitBounds(bounds);
    if (path.length === 1) {
      map.setZoom(15);
    }
  }

  function updatePlaybackMarker(p) {
    const map = mapRef.current;
    if (!map || !p) return;
    const pos = { lng: Number(p.longitude), lat: Number(p.latitude) };
    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position: pos,
        map,
        title: "轨迹回放"
      });
    } else {
      markerRef.current.setPosition(pos);
    }
    map.panTo(pos);
  }

  function stopTimer() {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }

  function play() {
    if (!points.length) return;
    setPlaying(true);
    stopTimer();
    const interval = Math.max(80, Math.floor(1000 / speed));
    timerRef.current = setInterval(() => {
      setIndex((prev) => {
        if (prev >= points.length - 1) {
          stopTimer();
          setPlaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, interval);
  }

  function pause() {
    setPlaying(false);
    stopTimer();
  }

  function reset() {
    pause();
    setIndex(0);
  }

  return (
    <div className="container-fluid">
      <div className="card shadow-sm status-card mt-3">
        <div className="card-header bg-secondary text-white">历史轨迹回放</div>
        <div className="card-body">
          <form className="row g-2 align-items-end" onSubmit={queryTrack}>
            <div className="col-md-2">
              <label className="form-label">设备ID</label>
              <input className="form-control" value={deviceId} onChange={(e) => setDeviceId(e.target.value)} />
            </div>
            <div className="col-md-2">
              <label className="form-label">设备号</label>
              <input className="form-control" value={deviceNo} onChange={(e) => setDeviceNo(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">开始时间</label>
              <input type="datetime-local" className="form-control" value={start} onChange={(e) => setStart(e.target.value)} required />
            </div>
            <div className="col-md-3">
              <label className="form-label">结束时间</label>
              <input type="datetime-local" className="form-control" value={end} onChange={(e) => setEnd(e.target.value)} required />
            </div>
            <div className="col-md-2">
              <button className="btn btn-primary w-100" type="submit" disabled={loading}>
                {loading ? "查询中..." : "查询轨迹"}
              </button>
            </div>
          </form>

          {error && <div className="status-banner error mt-3">{error}</div>}

          <div className="d-flex align-items-center gap-2 mt-3">
            <button className="btn btn-success btn-sm me-2" onClick={play} disabled={!points.length || playing}>播放</button>
            <button className="btn btn-warning btn-sm me-2" onClick={pause} disabled={!playing}>暂停</button>
            <button className="btn btn-outline-secondary btn-sm me-2" onClick={reset} disabled={!points.length}>重置</button>
            <label className="me-2">倍速</label>
            <select className="form-select form-select-sm" style={{ width: 120 }} value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
              <option value={1}>1x</option>
              <option value={2}>2x</option>
              <option value={4}>4x</option>
              <option value={8}>8x</option>
            </select>
          </div>

          <div className="mt-3 status-banner info" style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <span>原始点数：{rawPointCount}</span>
            <span>回放点数：{points.length}</span>
            <span>当前位置：{points.length ? `${index + 1}/${points.length}` : "-"}</span>
            <span>当前坐标：{currentPoint ? `${Number(currentPoint.longitude).toFixed(6)}, ${Number(currentPoint.latitude).toFixed(6)}` : "-"}</span>
            <span>当前时间：{toLocalDateTimeText(currentPoint?.gpsTime)}</span>
            <span>当前速度：{currentPoint?.speed ?? "-"}</span>
          </div>

          <div className="mt-3" style={{ position: "relative", height: "min(70vh, 760px)", minHeight: 620 }}>
            <div id="trackMapContainer" style={{ width: "100%", height: "100%", border: "1px solid #ddd", borderRadius: 8 }} />
            {!mapRef.current && (
              <div
                className="text-muted"
                style={{
                  position: "absolute",
                  left: 0,
                  top: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none"
                }}
              >
                <div className="status-banner warn">{mapError || "正在加载地图..."}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
